import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

/** Plans that expose subscription tiers (same source as checkout). */
function getPlansWithTiers(pricingPlans) {
    return (pricingPlans || []).filter(
        (p) => Array.isArray(p.tiers) && p.tiers.length > 0
    );
}

function formatTierLabel(tier) {
    if (!tier) return '';
    const parts = [tier.name, tier.interval_count, tier.payment_interval].filter(
        (x) => x !== undefined && x !== null && x !== ''
    );
    return parts.join(' - ');
}

export default function NewUser() {
    const { hasPermission } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        phone: '',
        role: 'user',
        businessId: '',
        pricingId: ''
    });
    const [users, setUsers] = useState([]);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        businessId: ''
    });
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [roles, setRoles] = useState([]);
    const [currentBusiness, setCurrentBusiness] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [businesses, setBusinesses] = useState([]);
    const [businessFormData, setBusinessFormData] = useState({
        name: '',
        code: '',
        seatsAvailable: '',
        pricingId: '',
        businessType: '',
        discountCode: ''
    });
    const [businessMsg, setBusinessMsg] = useState('');
    const [businessError, setBusinessError] = useState('');
    const [pricingPlans, setPricingPlans] = useState([]);
    const [discountCodes, setDiscountCodes] = useState([]);
    const [discountFormData, setDiscountFormData] = useState({
        code: '',
        percentage: '',
        description: '',
        max_uses: '',
        valid_until: '',
        duration: 'once',
        duration_in_months: '',
        applies_to_products: []
    });
    const [selectedTierByDiscountCode, setSelectedTierByDiscountCode] = useState({});
    const [discountMsg, setDiscountMsg] = useState('');
    const [discountError, setDiscountError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchUserRole();
        fetchUsers();
        fetchRoles();
        fetchPricingPlans();
        if (userRole === 'superadmin') {
            fetchBusinesses();
            fetchDiscountCodes();
        } else if (userRole !== 'superadmin') {
            fetchCurrentUserBusiness();
        }
    }, [userRole]);

    const fetchCurrentUserBusiness = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/current-business`,
                { withCredentials: true }
            );
            setCurrentBusiness(response.data);
            // Auto-set the businessId in formData
            setFormData(prev => ({
                ...prev,
                businessId: response.data.idbusiness
            }));
        } catch (error) {
            console.error('Error fetching current business:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                { withCredentials: true }
            );
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Kunne ikke laste brukere');
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/roles`,
                { withCredentials: true }
            );
            setRoles(response.data);
        } catch (error) {
            console.error('Error fetching roles:', error);
            setError('Kunne ikke laste roller');
        }
    };

    const fetchUserRole = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`,
                { withCredentials: true }
            );
            setUserRole(response.data.role);
        } catch (error) {
            console.error('Error fetching user role:', error);
        }
    };

    const fetchBusinesses = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses`,
                { withCredentials: true }
            );
            setBusinesses(response.data);
        } catch (error) {
            console.error('Error fetching businesses:', error);
            setError('Kunne ikke laste bedrifter');
        }
    };

    const fetchPricingPlans = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
                { withCredentials: true }
            );
            setPricingPlans(response.data);
        } catch (error) {
            console.error('Error fetching pricing plans:', error);
        }
    };

    const fetchDiscountCodes = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/discount-codes`,
                { withCredentials: true }
            );
            setDiscountCodes(response.data);
        } catch (error) {
            console.error('Error fetching discount codes:', error);
        }
    };

    const handleDiscountChange = (e) => {
        const { name, value } = e.target;
        setDiscountFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDiscountProductsChange = (e) => {
        const selectedProducts = Array.from(e.target.selectedOptions).map((option) => option.value);
        setDiscountFormData((prev) => ({
            ...prev,
            applies_to_products: selectedProducts
        }));
    };

    const handleDiscountSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/discount-codes`,
                {
                    ...discountFormData,
                    duration_in_months: discountFormData.duration === 'repeating'
                        ? discountFormData.duration_in_months
                        : null
                },
                { withCredentials: true }
            );

            if (response.status === 201 || response.status === 200) {
                setDiscountMsg('Rabattkode opprettet!');
                setDiscountFormData({
                    code: '',
                    percentage: '',
                    description: '',
                    max_uses: '',
                    valid_until: '',
                    duration: 'once',
                    duration_in_months: '',
                    applies_to_products: []
                });
                fetchDiscountCodes();
                setTimeout(() => setDiscountMsg(''), 3000);
            }
        } catch (error) {
            console.error('Error saving discount code:', error);
            setDiscountError(error.response?.data?.message || 'Kunne ikke lagre rabattkode');
            setTimeout(() => setDiscountError(''), 3000);
        }
    };

    const handleDeleteDiscount = async (id) => {
        if (!window.confirm('Er du sikker på at du vil slette denne rabattkoden?')) return;
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/discount-codes/${id}`,
                { withCredentials: true }
            );
            fetchDiscountCodes();
        } catch (error) {
            console.error('Error deleting discount code:', error);
            setDiscountError('Kunne ikke slette rabattkode');
            setTimeout(() => setDiscountError(''), 3000);
        }
    };

    const getAvailableTiersForDiscount = (discount) => {
        const appliesRaw = Array.isArray(discount.applies_to_products)
            ? discount.applies_to_products.map((x) => String(x).trim()).filter(Boolean)
            : [];
        const plans = getPlansWithTiers(pricingPlans);

        const priceIdSet = new Set(appliesRaw.filter((id) => id.startsWith('price_')));
        const pricingIdSet = new Set(
            appliesRaw.filter((id) => /^\d+$/.test(id)).map((id) => Number(id))
        );

        const hasPriceScope = priceIdSet.size > 0;
        const hasPricingIdScope = pricingIdSet.size > 0;

        const plansInScope =
            appliesRaw.length === 0
                ? plans
                : plans.filter((plan) => {
                      const tiers = plan.tiers || [];
                      const matchesPrice =
                          hasPriceScope &&
                          tiers.some((t) => t.stripe_price_id && priceIdSet.has(t.stripe_price_id));
                      const matchesPricingRow =
                          hasPricingIdScope && pricingIdSet.has(Number(plan.idpricing));
                      if (hasPriceScope || hasPricingIdScope) {
                          return matchesPrice || matchesPricingRow;
                      }
                      return true;
                  });

        return plansInScope.flatMap((plan) => {
            const tiers = Array.isArray(plan.tiers) ? plan.tiers : [];
            const priceScoped = hasPriceScope
                ? tiers.filter((t) => t.stripe_price_id && priceIdSet.has(t.stripe_price_id))
                : [];
            const useTiers = priceScoped.length > 0 ? priceScoped : tiers;
            return useTiers
                .filter((t) => t.stripe_price_id)
                .map((tier) => ({
                    productId: plan.idpricing,
                    productName: plan.name || 'Produkt',
                    tierId: tier.id,
                    tierLabel: formatTierLabel(tier),
                    amount: tier.amount,
                    paymentInterval: tier.payment_interval,
                    intervalCount: tier.interval_count,
                    stripePriceId: tier.stripe_price_id
                }));
        });
    };

    const formatDiscountScopeForDisplay = (discount) => {
        const ids = Array.isArray(discount.applies_to_products)
            ? discount.applies_to_products
            : [];
        if (ids.length === 0) return '';
        const plans = getPlansWithTiers(pricingPlans);
        const labels = ids.map((rawId) => {
            const id = String(rawId).trim();
            if (id.startsWith('price_')) {
                for (const plan of plans) {
                    const tier = (plan.tiers || []).find((t) => t.stripe_price_id === id);
                    if (tier) {
                        return `${plan.name} (${formatTierLabel(tier)})`;
                    }
                }
            }
            if (/^\d+$/.test(id)) {
                const pid = Number(id);
                const plan = plans.find((p) => Number(p.idpricing) === pid);
                if (plan) return plan.name;
            }
            return id;
        });
        return labels.join(', ');
    };

    const buildRegisterUrl = (discountCode, selectedTier, discountPercentage) => {
        const normalizedCode = String(discountCode || '').trim().toUpperCase();
        const params = new URLSearchParams();
        params.set('discount_code', normalizedCode);
        if (discountPercentage !== undefined && discountPercentage !== null && discountPercentage !== '') {
            params.set('discount_percentage', String(discountPercentage));
        }
        if (selectedTier?.tierId) {
            params.set('price_tier_id', String(selectedTier.tierId));
            if (selectedTier.productName) params.set('product_name', selectedTier.productName);
            if (selectedTier.tierLabel) params.set('tier_label', selectedTier.tierLabel);
            if (selectedTier.amount !== undefined && selectedTier.amount !== null) {
                params.set('price_amount', String(selectedTier.amount));
            }
        }
        return `${window.location.origin}/create-account?${params.toString()}`;
    };

    const handleShareDiscount = async (discountCode, selectedTier, discountPercentage) => {
        const normalizedCode = String(discountCode || '').trim().toUpperCase();
        if (!normalizedCode) return;
        if (!selectedTier?.tierId) {
            setDiscountError('Velg produkt/intervall før du deler registreringslenken');
            setTimeout(() => setDiscountError(''), 3000);
            return;
        }

        const shareUrl = buildRegisterUrl(normalizedCode, selectedTier, discountPercentage);
        const shareData = {
            title: 'Rabattkode Løpeglad',
            text: selectedTier?.productName
                ? `Bruk rabattkoden ${normalizedCode} når du registrerer deg for ${selectedTier.productName} (${selectedTier.tierLabel}).`
                : `Bruk rabattkoden ${normalizedCode} når du registrerer deg.`,
            url: shareUrl
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
                setDiscountMsg('Delingsdialog åpnet');
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
                setDiscountMsg('Delingslenke kopiert til utklippstavlen');
            } else {
                window.prompt('Kopier lenken:', shareUrl);
                setDiscountMsg('Delingslenke klar');
            }
            setTimeout(() => setDiscountMsg(''), 3000);
        } catch (error) {
            // Ignore user-cancelled share.
            if (error?.name === 'AbortError') return;
            setDiscountError('Kunne ikke dele rabattkode');
            setTimeout(() => setDiscountError(''), 3000);
        }
    };

    const handleCopyRegisterUrl = async (discountCode, selectedTier, discountPercentage) => {
        const normalizedCode = String(discountCode || '').trim().toUpperCase();
        if (!normalizedCode) return;
        if (!selectedTier?.tierId) {
            setDiscountError('Velg produkt/intervall før du kopierer registreringslenken');
            setTimeout(() => setDiscountError(''), 3000);
            return;
        }

        const registerUrl = buildRegisterUrl(normalizedCode, selectedTier, discountPercentage);

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(registerUrl);
                setDiscountMsg('Registreringslenke kopiert til utklippstavlen');
            } else {
                window.prompt('Kopier registreringslenken:', registerUrl);
                setDiscountMsg('Registreringslenke klar');
            }
            setTimeout(() => setDiscountMsg(''), 3000);
        } catch (error) {
            setDiscountError('Kunne ikke kopiere registreringslenke');
            setTimeout(() => setDiscountError(''), 3000);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                formData,
                { withCredentials: true }
            );

            if (response.status === 201) {
                setMsg('Bruker opprettet!');
                // If payment link exists, redirect user
                if (response.data.paymentLink) {
                    window.location.href = response.data.paymentLink;
                } else {
                    setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        password: '',
                        phone: '',
                        role: 'user',
                        businessId: currentBusiness?.idbusiness || '',
                        pricingId: ''
                    });
                    fetchUsers();
                    setTimeout(() => setMsg(''), 3000);
                }
            }
        } catch (error) {
            console.error('Error creating user:', error);
            setError(error.response?.data?.message || 'Kunne ikke opprette bruker');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleUserClick = (userId) => {
        navigate(`/users/${userId}`);
    };

    const filteredRoles = roles.filter(role => {
        if (hasPermission('canManageRoles')) return true;
        return role.role !== 'superadmin'; // Regular admins can't create superadmins
    });

    const handleBusinessChange = (e) => {
        const { name, value } = e.target;
        setBusinessFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleBusinessSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses`,
                businessFormData,
                { withCredentials: true }
            );

            if (response.status === 201) {
                setBusinessMsg('Bedrift opprettet!');
                setBusinessFormData({
                    name: '',
                    code: '',
                    seatsAvailable: '',
                    pricingId: '',
                    businessType: '',
                    discountCode: ''
                });
                fetchBusinesses();
                setTimeout(() => setBusinessMsg(''), 3000);
            }
        } catch (error) {
            console.error('Error creating business:', error);
            setBusinessError(error.response?.data?.message || 'Kunne ikke opprette bedrift');
            setTimeout(() => setBusinessError(''), 3000);
        }
    };

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <h1>Legg til ny bruker</h1>
            </div>

            {msg && <div className="success-message">{msg}</div>}
            {error && <div className="error-message">{error}</div>}

            {userRole === 'superadmin' && (
                <div className="section-container">
                    <h2>Opprett ny bedrift</h2>
                    {businessMsg && <div className="success-message">{businessMsg}</div>}
                    {businessError && <div className="error-message">{businessError}</div>}

                    <form className="form-container" onSubmit={handleBusinessSubmit}>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="businessName">Bedriftsnavn</label>
                                <input
                                    type="text"
                                    id="businessName"
                                    name="name"
                                    value={businessFormData.name}
                                    onChange={handleBusinessChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="businessCode">Bedriftskode</label>
                                <input
                                    type="text"
                                    id="businessCode"
                                    name="code"
                                    value={businessFormData.code}
                                    onChange={handleBusinessChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="seatsAvailable">Tilgjengelige plasser</label>
                                <input
                                    type="number"
                                    id="seatsAvailable"
                                    name="seatsAvailable"
                                    value={businessFormData.seatsAvailable}
                                    onChange={handleBusinessChange}
                                    required
                                    min="1"
                                />
                            </div>
{/* 
                            <div className="form-group">
                                <label htmlFor="pricingId">Prisplan</label>
                                <select
                                    id="pricingId"
                                    name="pricingId"
                                    value={businessFormData.pricingId}
                                    onChange={handleBusinessChange}
                                    required
                                    className="form-select"
                                >
                                    <option value="">Velg en prisplan</option>
                                    {pricingPlans.map(pricing => {
                                        let priceDisplay = '';
                                        if (pricing.tiers_mode) {
                                            const lowestTier = pricing.tiers && pricing.tiers[0];
                                            if (lowestTier) {
                                                priceDisplay = `from ${pricing.currency?.toUpperCase() || 'NOK'} ${lowestTier.amount}`;
                                            }
                                        } else {
                                            priceDisplay = `${pricing.currency?.toUpperCase() || 'NOK'} ${pricing.price}`;
                                        }

                                        return (
                                            <option 
                                                key={pricing.idpricing} 
                                                value={pricing.idpricing}
                                            >
                                                {pricing.name} - {priceDisplay} / {pricing.payment_interval || 'month'}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div> */}

                            <div className="form-group">
                                <label htmlFor="businessType">Bedriftstype</label>
                                <select
                                    id="businessType"
                                    name="businessType"
                                    value={businessFormData.businessType}
                                    onChange={handleBusinessChange}
                                    required
                                    className="form-select"
                                >
                                    <option value="">Velg en bedriftstype</option>
                                    <option value="parkinson">Parkinson</option>
                                    <option value="regular">Regular</option>
                                    <option value="health">Health</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="businessDiscountCode">Rabattkode (valgfritt)</label>
                                <input
                                    type="text"
                                    id="businessDiscountCode"
                                    name="discountCode"
                                    value={businessFormData.discountCode}
                                    onChange={handleBusinessChange}
                                    placeholder="Skriv inn rabattkode"
                                    className="form-input"
                                    style={{ textTransform: 'uppercase' }}
                                />
                                <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                    Rabattkoden vil bli brukt når bedriften oppretter abonnement
                                </small>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="submit-button">
                                Opprett bedrift
                            </button>
                        </div>
                    </form>

                    <div className="businesses-list">
                        <div className="section-header">
                            <h2>Eksisterende bedrifter</h2>
                            <Link to="/businesses" className="view-all-link">
                                Vis alle <i className="fas fa-arrow-right"></i>
                            </Link>
                        </div>
                        <div className="list-container">
                            {businesses.map((business) => (
                                <div 
                                    key={business.idbusiness} 
                                    className="list-item"
                                >
                                    <div className="business-info">
                                        <h3>{business.name}</h3>
                                        <p>Kode: {business.code}</p>
                                        <p>Tilgjengelige plasser: {business.seats_available}</p>
                                        {business.pricing_name && (
                                            <p className="business-pricing">
                                                Pris: {business.pricing_name}
                                            </p>
                                        )}
                                    </div>
                                    <Link 
                                        to={`/businesses/${business.idbusiness}`}
                                        className="view-details-button"
                                    >
                                        Vis detaljer
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {userRole === 'superadmin' && (
                <div className="section-container">
                    <h2>Administrer rabattkoder</h2>
                    {discountMsg && <div className="success-message">{discountMsg}</div>}
                    {discountError && <div className="error-message">{discountError}</div>}

                    <form className="form-container" onSubmit={handleDiscountSubmit}>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="discountCode">Rabattkode</label>
                                <input
                                    type="text"
                                    id="discountCode"
                                    name="code"
                                    value={discountFormData.code}
                                    onChange={handleDiscountChange}
                                    required
                                    style={{ textTransform: 'uppercase' }}
                                    placeholder="F.eks. SUMMER2024"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="discountPercentage">Rabatt prosent</label>
                                <input
                                    type="number"
                                    id="discountPercentage"
                                    name="percentage"
                                    value={discountFormData.percentage}
                                    onChange={handleDiscountChange}
                                    required
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    placeholder="10"
                                />
                                <small>F.eks. 10 for 10% rabatt</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="discountDescription">Beskrivelse</label>
                                <input
                                    type="text"
                                    id="discountDescription"
                                    name="description"
                                    value={discountFormData.description}
                                    onChange={handleDiscountChange}
                                    placeholder="Valgfritt"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="discountMaxUses">Maks antall bruk</label>
                                <input
                                    type="number"
                                    id="discountMaxUses"
                                    name="max_uses"
                                    value={discountFormData.max_uses}
                                    onChange={handleDiscountChange}
                                    min="1"
                                    placeholder="Tom = ubegrenset"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="discountValidUntil">Gyldig til (tidssone Norge)</label>
                                <input
                                    type="datetime-local"
                                    id="discountValidUntil"
                                    name="valid_until"
                                    value={discountFormData.valid_until}
                                    onChange={handleDiscountChange}
                                />
                                <small>
                                    Dato og klokkeslett tolkes som Europe/Oslo (norsk tid, inkl. sommertid).
                                    Bare dato: kupongen utløper ved slutten av den dagen (23:59:59).
                                </small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="discountDuration">Varighet</label>
                                <select
                                    id="discountDuration"
                                    name="duration"
                                    value={discountFormData.duration}
                                    onChange={handleDiscountChange}
                                    className="form-select"
                                >
                                    <option value="once">Kun første betaling</option>
                                    <option value="forever">For alltid</option>
                                    <option value="repeating">Gjentas i X måneder</option>
                                </select>
                            </div>

                            {discountFormData.duration === 'repeating' && (
                                <div className="form-group">
                                    <label htmlFor="discountDurationMonths">Antall måneder</label>
                                    <input
                                        type="number"
                                        id="discountDurationMonths"
                                        name="duration_in_months"
                                        value={discountFormData.duration_in_months}
                                        onChange={handleDiscountChange}
                                        min="1"
                                        required={discountFormData.duration === 'repeating'}
                                        placeholder="F.eks. 3"
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="discountAppliesToProducts">Gjelder produkter</label>
                                <select
                                    id="discountAppliesToProducts"
                                    multiple
                                    value={discountFormData.applies_to_products}
                                    onChange={handleDiscountProductsChange}
                                    className="form-select"
                                >
                                    {getPlansWithTiers(pricingPlans).flatMap((plan) =>
                                        (plan.tiers || [])
                                            .filter((t) => t.stripe_price_id)
                                            .map((tier) => (
                                                <option
                                                    key={`${plan.idpricing}-${tier.id}`}
                                                    value={tier.stripe_price_id}
                                                >
                                                    {`${plan.name} (${formatTierLabel(tier)})`}
                                                </option>
                                            ))
                                    )}
                                </select>
                                <small>Velg ingen for at kupongen skal gjelde alle priser (alle produkter).</small>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="submit-button">
                                Opprett rabattkode
                            </button>
                        </div>
                    </form>

                    <div className="discount-codes-list">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                            <h3>Eksisterende rabattkoder</h3>
                            <Link to="/discounts" className="edit-button" style={{ textDecoration: 'none' }}>
                                Full rabattoversikt
                            </Link>
                        </div>
                        <div className="list-container">
                            {discountCodes.length === 0 ? (
                                <p>Ingen rabattkoder opprettet ennå</p>
                            ) : (
                                discountCodes.map((discount) => (
                                    <div key={discount.iddiscount_code} className="list-item">
                                        <div className="discount-info">
                                            <h4>{discount.code}</h4>
                                            <p><strong>{discount.percentage}%</strong> rabatt</p>
                                            {discount.description && <p>{discount.description}</p>}
                                            <p>
                                                Brukt: {discount.used_count}
                                                {discount.max_uses && ` / ${discount.max_uses}`}
                                            </p>
                                            <p>
                                                Status: {discount.is_active ? 'Aktiv' : 'Inaktiv'}
                                            </p>
                                            <p>
                                                Varighet: {discount.duration === 'forever'
                                                    ? 'For alltid'
                                                    : discount.duration === 'repeating'
                                                        ? `${discount.duration_in_months || 0} måneder`
                                                        : 'Kun første betaling'}
                                            </p>
                                            {Array.isArray(discount.applies_to_products) && discount.applies_to_products.length > 0 && (
                                                <p>
                                                    Gjelder priser: {formatDiscountScopeForDisplay(discount)}
                                                </p>
                                            )}
                                            {discount.valid_until && (
                                                <p>
                                                    Til:{' '}
                                                    {new Date(discount.valid_until).toLocaleString('nb-NO', {
                                                        timeZone: 'Europe/Oslo',
                                                        dateStyle: 'short',
                                                        timeStyle: 'short',
                                                    })}
                                                </p>
                                            )}
                                        </div>
                                        <div className="discount-actions">
                                            <select
                                                className="form-select"
                                                value={selectedTierByDiscountCode[discount.code] || ''}
                                                onChange={(e) =>
                                                    setSelectedTierByDiscountCode((prev) => ({
                                                        ...prev,
                                                        [discount.code]: e.target.value
                                                    }))
                                                }
                                                style={{ marginBottom: '0.5rem' }}
                                            >
                                                <option value="">Velg produkt/intervall for registreringslenke</option>
                                                {getAvailableTiersForDiscount(discount).map((tier) => (
                                                    <option key={`${discount.code}-${tier.tierId}`} value={tier.tierId}>
                                                        {`${tier.productName} (${tier.tierLabel}) - NOK ${tier.amount}`}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={() => {
                                                    const selectedTier = getAvailableTiersForDiscount(discount)
                                                        .find((tier) => String(tier.tierId) === String(selectedTierByDiscountCode[discount.code]));
                                                    handleShareDiscount(discount.code, selectedTier, discount.percentage);
                                                }}
                                                className="edit-button"
                                            >
                                                Del
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const selectedTier = getAvailableTiersForDiscount(discount)
                                                        .find((tier) => String(tier.tierId) === String(selectedTierByDiscountCode[discount.code]));
                                                    handleCopyRegisterUrl(discount.code, selectedTier, discount.percentage);
                                                }}
                                                className="edit-button"
                                            >
                                                Kopier registreringslenke
                                            </button>
                                            <button
                                                onClick={() => handleDeleteDiscount(discount.iddiscount_code)}
                                                className="delete-button"
                                            >
                                                Slett
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            <form className="form-container" onSubmit={handleSubmit}>
                <div className="form-grid">
                    <div className="form-group">
                        <label htmlFor="firstName">Fornavn</label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="lastName">Etternavn</label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">E-post</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Telefonnummer</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+47 XXX XX XXX"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Passord</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="role">Rolle</label>
                        <select
                            id="role"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Velg en rolle</option>
                            {filteredRoles.map(role => (
                                <option key={role.idrole} value={role.role}>
                                    {role.role}
                                </option>
                            ))}
                        </select>
                    </div>

                    {!formData.businessId && (
                        <div className="form-group">
                            <label htmlFor="pricingId">Prisplan</label>
                            <select
                                id="pricingId"
                                name="pricingId"
                                value={formData.pricingId}
                                onChange={handleChange}
                                required={!formData.businessId}
                                className="form-select"
                            >
                                <option value="">Velg en prisplan</option>
                                <option value="null">Gratis plan</option>
                                {pricingPlans.map(pricing => {
                                    let priceDisplay = '';
                                    if (pricing.tiers_mode) {
                                        const lowestTier = pricing.tiers && pricing.tiers[0];
                                        if (lowestTier) {
                                            priceDisplay = `from ${pricing.currency?.toUpperCase() || 'NOK'} ${lowestTier.amount}`;
                                        }
                                    } else {
                                        priceDisplay = `${pricing.currency?.toUpperCase() || 'NOK'} ${pricing.price}`;
                                    }

                                    return (
                                        <option 
                                            key={pricing.idpricing} 
                                            value={pricing.idpricing}
                                        >
                                            {pricing.name} - {priceDisplay} / {pricing.payment_interval || 'month'}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    {hasPermission('canManageAllBusinesses') ? (
                        <div className="form-group">
                            <label htmlFor="businessId">Bedrift</label>
                            <select
                                id="businessId"
                                name="businessId"
                                value={formData.businessId}
                                onChange={(e) => {
                                    handleChange(e);
                                    // Clear pricing if business is selected
                                    if (e.target.value) {
                                        setFormData(prev => ({ ...prev, pricingId: '' }));
                                    }
                                }}
                            >
                                <option value="">Velg en bedrift</option>
                                {businesses.map(business => (
                                    <option key={business.idbusiness} value={business.idbusiness}>
                                        {business.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : currentBusiness ? (
                        <div className="form-group">
                            <label>Bedrift</label>
                            <input
                                type="text"
                                value={currentBusiness.name}
                                disabled
                                className="disabled-input"
                            />
                        </div>
                    ) : null}
                </div>

                <div className="form-actions">
                    <button type="submit" className="submit-button">
                        Opprett bruker
                    </button>
          
                    <button 
                        className="cancel-button"
                        onClick={() => navigate('/dashboard')}
                    >
                        Avbryt
                    </button>

                </div>
            </form>

            <div className="users-list">
                <div className="section-header">
                    <h2>Nylige brukere</h2>
                    <Link to="/users" className="view-all-link">
                        Vis alle <i className="fas fa-arrow-right"></i>
                    </Link>
                </div>
                <div className="list-container">
                    {users.slice(0, 3).map((user) => (
                        <div 
                            key={user.idusers} 
                            className="list-item clickable"
                            onClick={() => handleUserClick(user.idusers)}
                        >
                            <div className="user-info">
                                <h3>{user.name}</h3>
                                <p className="user-email">{user.email}</p>
                                <p className="user-role">Rolle: {user.role}</p>
                                <p className="user-business">Bedrift: {user.businessName}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}